import { useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  XCircleIcon,
  DocumentCheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

export const ItemListActionBar = ({
  itemList,
  onApprove,
  onReject,
  onSubmitForApproval,
  onDelete,
  onMarkInvoicedManually,
  show = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(show);

  useEffect(() => {
    setOpen(show);
  }, [show]);

  if (!itemList) return null;

  // Determine which actions to show based on status and permissions
  const showSubmitForApproval =
    itemList.can_edit && itemList.status === "draft";
  const showApproveReject =
    itemList.can_approve && itemList.status === "pending_approval";
  const showDelete = itemList.can_delete;
  const showMarkInvoiced =
    typeof onMarkInvoicedManually === "function" &&
    itemList.can_mark_invoiced_manually;

  // Don't show bar if no actions are available
  if (
    !showSubmitForApproval &&
    !showApproveReject &&
    !showDelete &&
    !showMarkInvoiced
  ) {
    return null;
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-x-0 bottom-0 pb-2 sm:pb-5 z-10 drop-shadow-xl">
          <div className="mx-auto max-w-5xl px-2 sm:px-6 lg:px-8">
            <div className="rounded-lg bg-white p-2 shadow-lg sm:p-3 border-2 border-blue-200">
              <div className="flex flex-wrap items-center justify-between">
                <div className="order-1 mb-2 sm:mb-0 sm:order-1 w-full sm:w-auto">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">
                      {t("project.item_list.status.label")}:{" "}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        itemList.status === "draft"
                          ? "bg-gray-100 text-gray-800"
                          : itemList.status === "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : itemList.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : itemList.status === "invoiced_manually"
                          ? "bg-indigo-100 text-indigo-900"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {t(`project.item_list.status.${itemList.status}`, {
                        defaultValue: itemList.status,
                      })}
                    </span>
                  </div>
                </div>
                <div className="order-2 mt-2 w-full flex-shrink-0 sm:order-2 sm:mt-0 sm:w-auto flex flex-wrap gap-2 justify-end">
                  {showSubmitForApproval && (
                    <button
                      onClick={onSubmitForApproval}
                      className="flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                      type="button"
                    >
                      <DocumentCheckIcon className="h-4 w-4 mr-2" />
                      {t("project.item_list.submit_for_approval")}
                    </button>
                  )}
                  {showApproveReject && (
                    <>
                      <button
                        onClick={onApprove}
                        className="flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
                        type="button"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        {t("project.item_list.approve")}
                      </button>
                      <button
                        onClick={onReject}
                        className="flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
                        type="button"
                      >
                        <XCircleIcon className="h-4 w-4 mr-2" />
                        {t("project.item_list.reject")}
                      </button>
                    </>
                  )}
                  {showMarkInvoiced && (
                    <button
                      onClick={onMarkInvoicedManually}
                      className="flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                      type="button"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      {t("project.item_list.mark_invoiced_manually")}
                    </button>
                  )}
                  {showDelete && (
                    <button
                      onClick={onDelete}
                      className="flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
                      type="button"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      {t("project.item_list.delete")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>
  );
};

